import { SaveStatus } from 'booking-creator/reducers/validation/types';
import {
  BookingMethods,
  BookingStateChangeTypes,
  BookingStatusNames,
  BookingType,
  CustomerMode,
  CustomerTypes,
  DiaryMobileVersion,
  IBooking,
  IBookingFromPouch,
  IBookingStatus,
  IBookingTag,
  ICustomer,
  IDiarySchedule,
  IPaymentDetailsBase,
  IPaymentSummary,
  IPouchServicePaymentOption,
  ISavedBookingMenuOption,
  ISavedBookingSelectedOptions,
  servicePaymentType,
} from 'shared-types/index';
import { cloneDeep, isEmpty } from 'lodash-es';
import NetworkConnectionService from 'external-services/network-connection-service';
import { StatusService, StatusServiceInstance } from 'external-services/status-service/index';
import { DiaryRootState } from 'appRoot/rootReducer';
import TagsService, { hardCodedTags } from 'external-services/tags-service';
import BookingUpsellOptionsService from 'external-services/booking-upsell-options-service/bookingUpsellOptions.service';
import CustomerValidationService from 'external-services/customer-validation-service/customerValidation.service';
import { CustomerGeneratorService } from 'external-services/customer-generator-service';
import PhoneNumberService from 'external-services/phone-number-service';
import { phoneNumberTypes } from 'external-services/phone-number-service/phoneNumber.types';
import {
  CREATE_NEW_CUSTOMER_VALIDATION_MESSAGE,
  EXISTING_CUSTOMER_VALIDAITON_MESSAGE,
} from 'external-services/customer-validation-service/types';
import {
  BookingCreatorPanelType,
  BookingValidationItems,
  IValidationErrorListItem,
  ValidationErrorType,
} from 'external-services/booking-validation-service/bookingValidation.types';
import { IBookingOptionsUpsellState } from 'booking-creator/reducers/bookingOptionsUpsell/types';
import BookingsUtilsService from 'external-services/bookings-utils-service';
import BookingUpdateService from 'external-services/booking-update-service';
import { IRecurringBookingState, RecurringBookingConfirmStatus } from 'booking-creator/reducers/recurringBooking/types';
import { BookingMode } from 'booking-creator/reducers/display/types';

const NS = 'validationSlice';

export function checkForInlineCustomer(customer: ICustomer): void {
  // if no email or phone provided - do not save customer info - dont call customer api
  if (!customer.email && !customer.phone) {
    if (!Object.isExtensible(customer)) {
      customer = cloneDeep(customer);
    }
    customer.customerType = CustomerTypes.inline;
  }
}

export function checkForWalkinCustomerDetail(customer: ICustomer, booking: IBooking): void {
  /**
   * walkin can has only first name and phone, email.
   * should be remove last name even if user inputs last name
   */
  if (booking.method === BookingMethods.walkin && customer.customerType === CustomerTypes.inline) {
    booking.lastName && delete booking.lastName;
    customer.lastName && delete customer.lastName;
  }
}

export function setNonStandbyBookingType(booking: IBooking): void {
  if (booking.tables?.length && booking.bookingType !== BookingType.StandbyList) {
    booking.bookingType = BookingType.Booking;
  } else if (booking.bookingType === BookingType.Booking) {
    // if is a booking, but table was removed, converts to waitList
    booking.bookingType = BookingType.WaitList;
  }
}

export function updateCustomerWithPaymentCustomerDetails(): void {
  // var tempCustomer = cloneDeep(vm.customer);
  // if (PaymentValidationService.isPaymentEmailUpdateChecked) {
  //   tempCustomer.email = PaymentValidationService.paymentEmail;
  // }
  // if (PaymentValidationService.isPaymentPhoneUpdateChecked) {
  //   tempCustomer.phone = PaymentValidationService.paymentPhone;
  // }
  // tempCustomer = PhoneNumberService.resetPhoneNumberWithTypeValidation(tempCustomer); // Remove country prefix when national number contains special char and a~z A~Z
  // if (PaymentValidationService.isPaymentEmailUpdateChecked || PaymentValidationService.isPaymentPhoneUpdateChecked) {
  //   CustomerUpdateService.update(tempCustomer);
  // }
}

export async function moveBookingToAnotherDate(
  savableBooking: IBooking,
  originalBooking: IBooking,
  tempCustomer: ICustomer,
  activeSchedule: IDiarySchedule,
  statusOptions: IBookingStatus[],
  staffValue: string,
  forceUpdateCustomer: boolean = false
): Promise<{
  savedBooking: IBooking | IBookingFromPouch;
  saveState: SaveStatus;
}> {
  // Since we're going to be creating a new booking without old booking data, we need to remove _rev and _id to avoid PouchDB conflict
  BookingsUtilsService.clearBookingIdentifiers(savableBooking);

  savableBooking.staffName = staffValue; // @todo: check if this is needed

  const isRecurringBooking = false; // !isEmpty(vm.bookingList) && !isEmpty(vm.requestParameters);
  let savedBooking: IBooking | IBookingFromPouch;
  let saveState: SaveStatus = SaveStatus.Pending;

  try {
    savedBooking = await BookingUpdateService.add(
      savableBooking,
      tempCustomer,
      originalBooking,
      activeSchedule,
      isRecurringBooking,
      forceUpdateCustomer
    );
  } catch (err) {
    saveState = SaveStatus.SaveFailed;
    console.warn(NS, 'BookingUpdateService.updateFromAnywhere', err);
  }

  if (savedBooking) {
    // we get the customer id for newly created customer as well and pass to recurring booking
    // if (isRecurringBooking) {
    //   vm.requestParameters.bookings = RecurringBookingService.updateRecurringBookingsProps(vm.requestParameters.bookings, vm.booking);
    //   RecurringBookingService.updateRecurringBookingsCustomer(vm.requestParameters.recurringBooking, tempCustomer, booking.customerId);
    //   console.log('vm.requestParameters', vm.requestParameters);
    //   await RecurringBookingService.SaveRecurringBooking(vm.requestParameters);
    //   BookingNotificationsService.sendNotification(booking, BookingNotificationsService.NEW);
    // }

    // @todo: check if this happens automatically
    // if ((savableBooking.bookingType === BookingType.Booking && savableBooking.paymentDetails?.paymentType !== servicePaymentType.noPayment)) {
    //   generateBookingUrl();
    //   vm.state = vm.SHOW_PAYMENT_URL;
    // } else {
    //   vm.state = vm.SUCCESS;
    // }

    try {
      await BookingUpdateService.setToCancelled(
        cloneDeep(originalBooking) as IBooking,
        staffValue,
        BookingStateChangeTypes.DATE_CHANGED,
        () => statusOptions.find((s) => s.name === BookingStatusNames.Cancelled)
      );
    } catch (err) {
      saveState = SaveStatus.SaveFailed;
      // @todo: may have to handle this better
      console.warn(NS, 'BookingUpdateService.setToCancelled', err);
    }
  }

  return { saveState, savedBooking };
}

export function saveValidationCheck({
  validationReducer,
  bookingOptionsUpsellReducer,
  displayReducer,
  settingReducer,
}: DiaryRootState): SaveStatus {
  if (validationReducer.canSave) {
    const isOnline = NetworkConnectionService.isOnline();
    // prettier-ignore
    const isPlusVersion = settingReducer.mobileVersion === DiaryMobileVersion.PlusVersion;
    // Ignore this validation if bookingType is WaitList
    if (
      displayReducer.activeBooking.bookingType === BookingType.WaitList ||
      displayReducer.activeBooking.method === BookingMethods.walkin
    ) {
      return SaveStatus.ValidationSuccessful;
    }

    if (
      isPlusVersion &&
      bookingOptionsUpsellReducer.isBookingOption &&
      !bookingOptionsUpsellReducer.bookingOptionsValid &&
      isOnline
    ) {
      // @todo: may need to scroll to booking options
      return SaveStatus.Pending;
    }

    if (
      isPlusVersion &&
      bookingOptionsUpsellReducer.isUpsellOptions &&
      !bookingOptionsUpsellReducer.upsellOptionsValid &&
      isOnline
    ) {
      // @todo: may need to scroll to upsell options
      return SaveStatus.Pending;
    }

    // if (recurringBooking.openRepeatBookingTab && !recurringBooking.isRecurringBookingSaved) {
    // @todo: may need to scroll to recurring booking
    //   return SaveStatus.Pending;
    // }

    // if (displayReducer.activeBooking?.paymentDetails && paymentReducer.isPaymentTypeChanged) {
    // @todo: scroll to payments panel again if payment has changed automatically, so user can confirm
    // show payment panel if the user hasn't checked it before saving, otherwise proceed to save
    //   return SaveStatus.Pending;
    // }

    return SaveStatus.ValidationSuccessful;
  } else {
    // @todo: scroll to validation errors
    return SaveStatus.ValidationFailed;
  }
}

export function getRecurringBookingMessage({
  recurringBookingReducer,
}: {
  recurringBookingReducer: IRecurringBookingState;
}): BookingValidationItems[] {
  const validationList: BookingValidationItems[] = [];

  if (
    recurringBookingReducer.recurringBookingConfirmationStatus ===
    RecurringBookingConfirmStatus.WaitingForConfirmation
  ) {
    validationList.push(BookingValidationItems.recurringBookingNotConfirmed);
  }

  return validationList;
}

export function getOptionsValidationMessages({
  bookingOptionsUpsellReducer,
  bookingType,
  method,
  isNewBooking,
  isPlusVersion,
}: {
  bookingOptionsUpsellReducer: IBookingOptionsUpsellState;
  bookingType?: BookingType;
  method?: BookingMethods;
  isNewBooking?: boolean;
  isPlusVersion?: boolean;
}): BookingValidationItems[] {
  const isOnline = NetworkConnectionService.isOnline();
  const validationList: BookingValidationItems[] = [];

  // Ignore this validation if booking type is Wailist
  if ((bookingType === BookingType.WaitList || method === BookingMethods.walkin) && isNewBooking)
    return [];

  if (
    bookingOptionsUpsellReducer.isBookingOption &&
    !bookingOptionsUpsellReducer.bookingOptionsValid &&
    isOnline
  ) {
    validationList.push(BookingValidationItems.bookingOptions);
  }
  // toggle on, and edit booking and pax threshold is invalid
  if (
    !bookingOptionsUpsellReducer.isPaxThresholdValidForEdit &&
    !isNewBooking &&
    bookingOptionsUpsellReducer.showBookingOptions
  ) {
    validationList.push(BookingValidationItems.bookingOptionsPax);
  }
  // comment follow as upsell no requirement
  // if (
  //   bookingOptionsUpsellReducer.isUpsellOptions &&
  //   !bookingOptionsUpsellReducer.upsellOptionsValid &&
  //   isOnline
  // ) {
  //   validationList.push(BookingValidationItems.upsellOptions);
  // }

  return validationList;
}

export function addPendingPaymentStatusToBooking(activeBooking: IBooking): void {
  if (StatusService.hasPayment(activeBooking.paymentDetails)) {
    const pendingPaymentStatus = StatusServiceInstance.getPaymentPendingStatus();
    if (pendingPaymentStatus) {
      activeBooking.status = pendingPaymentStatus;
    }
  }
}

export function normaliseOptionsForSave(
  activeBooking: IBooking,
  allBookingOptions: IPouchServicePaymentOption[]
): void {
  let selectedOptionsFlattened: ISavedBookingMenuOption[] = [];
  let savableOptions: ISavedBookingSelectedOptions[] = [];

  // if BO or Upsell is present then we saving the selectedMenuOptions in state and taking value from there before saving.
  if (activeBooking && !isEmpty(activeBooking.selectedMenuOptions)) {
    selectedOptionsFlattened = BookingUpsellOptionsService.getFlatExtras(
      activeBooking.selectedMenuOptions
    );
  }

  if (selectedOptionsFlattened.length > 0) {
    savableOptions = BookingUpsellOptionsService.updateSelectedOptions(
      selectedOptionsFlattened,
      allBookingOptions
    );
  }
  delete activeBooking.selectedMenuOptions; // not needed for save
  activeBooking.selectedOptions = savableOptions; // these are used for saving
}

export function normaliseTagsForSave(savableBooking: IBooking, tags: IBookingTag[]): void {
  // we never need to show the delivery tag for new bookings. It is only ever needed on booking options when in Takeaway mode
  const allTags = tags.filter((t) => t._id !== hardCodedTags.DELIVERY_TAG_ID);

  savableBooking.tags = TagsService.mergeAndGetUniqueTags(
    savableBooking.selectedOptions,
    allTags,
    savableBooking.tags
  ); // Getting the tags from BO and children
}

export function getPaymentMessage(
  paymentSummary: IPaymentSummary,
  paymentDetails: IPaymentDetailsBase,
  isInlineCustomer = false
): string {

  if (isInlineCustomer && paymentDetails && paymentDetails.paymentType !== servicePaymentType.noPayment) {
    return 'Payments cannot be added to bookings where the customer has no email and phone';
  }

  if (
    !paymentSummary &&
    paymentDetails &&
    paymentDetails.paymentType !== servicePaymentType.noPayment &&
    !paymentDetails.price
  ) {
    return 'Invalid payment details.';
  }

  //@todo: when payment url component has been completed
  // else if (
  //   !PaymentValidationService.isPaymentPhoneValid ||
  //   !PaymentValidationService.isPaymentEmailValid
  // ) {
  //   return 'Invalid payment contact details';
  // }
  return '';
}

export function saveRecurringBooking() {}

export function checkCustomerValidationHelper(
  state: DiaryRootState,
  ignorePhoneEmailValid: boolean = false
) {
  const { displayReducer, venueReducer, customerReducer, recurringBooking, validationReducer, settingReducer } =
    state;
  const { activeBooking, mode } = displayReducer;
  const {customer, customers, customerView} = customerReducer;
  const {mobileVersion} = settingReducer;
  const tempCustomer = cloneDeep(customer); // we will need to modify it so must make a copy

  const isNewCustomer = customers?.length === 0;
  const isWalkin = activeBooking.method === BookingMethods.walkin;
  const isPlusVersion = mobileVersion === DiaryMobileVersion.PlusVersion;

  /**
   * @todo
   * Horita
   * need to check again later
   * https://nowbookit.atlassian.net/browse/UI-510?focusedCommentId=78703
   *
   * paymentDetails can be null.
   */
  const isNoPayment = activeBooking.paymentDetails?.paymentType === servicePaymentType.noPayment;
  const customerEmailCheck = CustomerValidationService.emailValidation(
    tempCustomer,
    isNoPayment,
    recurringBooking.openRepeatBookingTab,
    false, // will always be false for Diary Mobile
    activeBooking.bookingType === BookingType.StandbyList,
    venueReducer.standByListEmail,
    activeBooking.isBookedBy,
    customer.customerType === CustomerTypes.inline
  );

  const phoneNumberTooShort = CustomerGeneratorService.isPhoneNumberTooShortForSearch(
    customerReducer.phoneWithoutCountryCode
  );

  // always going to be true because phone number gets forced to number
  // const isPhoneNumberType = CustomerValidationService.checkIsPhoneNumberType(tempCustomer.phone);

  let isPhoneValid = false;
  if (!phoneNumberTooShort) {
    const formattedPhone = PhoneNumberService.normalizePhoneNumberWithoutCountryCode(
      tempCustomer.phone
    );
    if (formattedPhone) {
      tempCustomer.phone = formattedPhone;
      isPhoneValid = true;
    }
  }

  const phoneValidationError = CustomerValidationService.getPhoneValidationError(
    phoneNumberTooShort,
    customerReducer.phoneWithoutCountryCode,
    tempCustomer.email,
    customerReducer.phoneTouched,
    isPhoneValid,
    true,
    isNoPayment,
    recurringBooking.openRepeatBookingTab,
    false, // will always be false for Diary Mobile
    customerReducer.phoneTouched,
    activeBooking.bookingType === BookingType.StandbyList,
    venueReducer.standByListSMS,
    null,
    customer.customerType === CustomerTypes.inline
  );

  const { emailRequired, ignorePhoneValidation } =
    CustomerValidationService.checkRulesByBookingTypeAndCustomerType(
      activeBooking.bookingType,
      venueReducer.standByListEmail,
      venueReducer.standByListSMS,
      tempCustomer.customerType as CustomerTypes
    );

  const customerDetailsValidation = CustomerValidationService.validateCustomer(
    tempCustomer,
    customerReducer.originalCustomer,
    emailRequired,
    ignorePhoneValidation
  );



  const isPhoneEmailValid = ignorePhoneEmailValid
    ? true
    : CustomerValidationService.validatePhoneEmailForExistingCustomer(
        tempCustomer,
        customerReducer.originalCustomer
      );

  let customerValidation =
    customerEmailCheck.valid &&
    isPhoneEmailValid &&
    !phoneValidationError &&
    customerDetailsValidation;

  const cantSaveMessageList: IValidationErrorListItem[] = [];

  if (!customerValidation || !customerValidation.isValid) {
    if (customerEmailCheck.msg) {
      cantSaveMessageList.push({
        msg: customerEmailCheck.msg,
        type: ValidationErrorType.email,
        panel: BookingCreatorPanelType.customerDetails,
      });
    }
    if (!customerDetailsValidation.isValid) {
      cantSaveMessageList.push({
        msg: 'You must enter a first and or last name.',
        type: ValidationErrorType.customer,
        panel: BookingCreatorPanelType.customerDetails,
      });
    }
    if (phoneValidationError) {
      cantSaveMessageList.push({
        msg: phoneValidationError,
        type: ValidationErrorType.phone,
        panel: BookingCreatorPanelType.customerDetails,
      });
    }

    if (!isPhoneEmailValid) {
      cantSaveMessageList.push({
        msg: EXISTING_CUSTOMER_VALIDAITON_MESSAGE,
        type: ValidationErrorType.existingCustomer,
        panel: BookingCreatorPanelType.customerDetails,
      });
    }
  }
  else {
    if (isPlusVersion && !isWalkin) {
      const isEmailOrPhonePresent = customer?.email || customer?.phone;
      if (mode === BookingMode.newBooking && customerView === CustomerMode.SEARCH && isNewCustomer && isEmailOrPhonePresent) {
        customerValidation = { ...customerValidation, isValid: false };
        cantSaveMessageList.push({
          msg: CREATE_NEW_CUSTOMER_VALIDATION_MESSAGE,
          type: ValidationErrorType.customer,
          panel: BookingCreatorPanelType.customerDetails,
        });
      }
    }

  }
  // for booked by validation
  const isCustomerValid = customerValidation ? customerValidation.isValid : false;
  // let isBookedByValid = true;
  let isPaymentValid = true;
  if (!validationReducer.isPaymentPhoneValid || !validationReducer.isPaymentEmailValid) {
    cantSaveMessageList.push({
      msg: 'Invalid payment contact details',
      type: ValidationErrorType.payment,
      panel: BookingCreatorPanelType.payment,
    });
    isPaymentValid = false;
  }
  if (activeBooking.isBookedBy) {
    if (!validationReducer.isBookedByValid) {
      validationReducer.bookedByErrors.forEach((error) => {
        cantSaveMessageList.push({
          msg: error,
          type: ValidationErrorType.bookedBy,
          panel: BookingCreatorPanelType.bookedBy,
        });
      });
    }
    if (
      !validationReducer.isBookedByPaymentEmailValid ||
      !validationReducer.isBookedByPaymentPhoneValid
    ) {
      cantSaveMessageList.push({
        msg: 'Invalid BookedBy payment contact details',
        type: ValidationErrorType.payment,
        panel: BookingCreatorPanelType.payment,
      });
      isPaymentValid = false;
    }
  }
  return {
    cantSaveMessageList,
    // for booked by, check whether active booked by and validate it if enable
    isValid:
      isCustomerValid &&
      (!activeBooking.isBookedBy || validationReducer.isBookedByValid) &&
      isPaymentValid,
  };
}
